import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { toast } from '../../hooks/use-toast';
import {
  ArrowLeft, Send, Bell, Users, TrendingUp, History,
  RefreshCw, Smartphone, CheckCircle, XCircle, Image,
  Link as LinkIcon, Tag, Megaphone
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PushAdmin = () => {
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();
  const [stats, setStats] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('send');
  
  // Form state
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('/dashboard');
  const [icon, setIcon] = useState('/logo.png');
  const [image, setImage] = useState('');

  useEffect(() => {
    if (!authLoading && (!currentUser || currentUser.role !== 'admin')) {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [currentUser, authLoading, navigate]);

  const getAuthHeader = () => {
    const token = localStorage.getItem('notimax_token');
    return { Authorization: `Bearer ${token}` };
  };

  const loadData = async () => {
    try {
      const [statsRes, notificationsRes] = await Promise.all([
        axios.get(`${API_URL}/api/push/stats`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/push/notifications`, { headers: getAuthHeader() })
      ]);
      setStats(statsRes.data);
      setNotifications(notificationsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendNotification = async () => {
    if (!title.trim() || !body.trim()) {
      toast({
        title: 'Error',
        description: 'El título y mensaje son requeridos',
        variant: 'destructive'
      });
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API_URL}/api/push/send`, {
        title,
        body,
        url,
        icon,
        image: image || null,
        target: 'all'
      }, { headers: getAuthHeader() });

      toast({
        title: '¡Notificación enviada!',
        description: response.data.message
      });

      // Clear form
      setTitle('');
      setBody('');
      setUrl('/dashboard');
      setImage('');

      // Reload data
      setTimeout(loadData, 2000);
    } catch (error) {
      console.error('Error sending notification:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo enviar la notificación',
        variant: 'destructive'
      });
    } finally {
      setSending(false);
    }
  };

  const generateVapidKeys = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/push/generate-vapid-keys`, {}, {
        headers: getAuthHeader()
      });
      
      toast({
        title: 'Claves VAPID generadas',
        description: 'Revisa la consola para ver las claves'
      });
      
      console.log('=== VAPID KEYS ==');
      console.log('Agrega estas líneas a /app/backend/.env:');
      console.log(`VAPID_PUBLIC_KEY=${response.data.VAPID_PUBLIC_KEY}`);
      console.log(`VAPID_PRIVATE_KEY=${response.data.VAPID_PRIVATE_KEY}`);
      console.log('================');
      
      alert(`Claves generadas!\n\nVAPID_PUBLIC_KEY=${response.data.VAPID_PUBLIC_KEY}\n\nVAPID_PRIVATE_KEY=...\n\nRevisa la consola del navegador para copiar las claves completas.`);
    } catch (error) {
      console.error('Error generating keys:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron generar las claves',
        variant: 'destructive'
      });
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate('/admin')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Volver
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center">
                  <Bell className="w-5 h-5 mr-2 text-blue-600" />
                  Notificaciones Push
                </h1>
                <p className="text-sm text-gray-500">Envía notificaciones a tus usuarios</p>
              </div>
            </div>
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" /> Actualizar
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Suscriptores Activos</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.active_subscribers || 0}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Notificaciones Enviadas</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total_notifications_sent || 0}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Send className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Campañas Enviadas</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total_campaigns || 0}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Megaphone className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Fallidas</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total_failed || 0}</p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 mb-6">
          <Button
            variant={activeTab === 'send' ? 'default' : 'outline'}
            onClick={() => setActiveTab('send')}
          >
            <Send className="w-4 h-4 mr-2" /> Enviar Notificación
          </Button>
          <Button
            variant={activeTab === 'history' ? 'default' : 'outline'}
            onClick={() => setActiveTab('history')}
          >
            <History className="w-4 h-4 mr-2" /> Historial
          </Button>
          <Button
            variant={activeTab === 'settings' ? 'default' : 'outline'}
            onClick={() => setActiveTab('settings')}
          >
            <Smartphone className="w-4 h-4 mr-2" /> Configuración
          </Button>
        </div>

        {/* Send Notification */}
        {activeTab === 'send' && (
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Nueva Notificación</CardTitle>
                <CardDescription>Envía una notificación push a todos los suscriptores</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="title">Título *</Label>
                  <Input
                    id="title"
                    placeholder="Ej: ¡Nueva promoción!"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    maxLength={50}
                  />
                  <p className="text-xs text-gray-500 mt-1">{title.length}/50 caracteres</p>
                </div>

                <div>
                  <Label htmlFor="body">Mensaje *</Label>
                  <Textarea
                    id="body"
                    placeholder="Ej: Aprovecha 20% de descuento en tu próxima cita"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={3}
                    maxLength={200}
                  />
                  <p className="text-xs text-gray-500 mt-1">{body.length}/200 caracteres</p>
                </div>

                <div>
                  <Label htmlFor="url" className="flex items-center">
                    <LinkIcon className="w-4 h-4 mr-1" /> URL al hacer clic
                  </Label>
                  <Input
                    id="url"
                    placeholder="/dashboard"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="image" className="flex items-center">
                    <Image className="w-4 h-4 mr-1" /> Imagen (opcional)
                  </Label>
                  <Input
                    id="image"
                    placeholder="https://ejemplo.com/imagen.jpg"
                    value={image}
                    onChange={(e) => setImage(e.target.value)}
                  />
                </div>

                <Button
                  onClick={sendNotification}
                  disabled={sending || !title.trim() || !body.trim()}
                  className="w-full bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600"
                >
                  {sending ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Enviar a {stats.active_subscribers || 0} suscriptores
                </Button>
              </CardContent>
            </Card>

            {/* Preview */}
            <Card>
              <CardHeader>
                <CardTitle>Vista Previa</CardTitle>
                <CardDescription>Así se verá la notificación</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-900 rounded-xl p-4 text-white max-w-sm mx-auto shadow-2xl">
                  <div className="flex items-start space-x-3">
                    <img src="/logo.png" alt="" className="w-10 h-10 rounded-lg" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{title || 'Título de la notificación'}</p>
                      <p className="text-gray-300 text-sm mt-1 line-clamp-2">{body || 'Mensaje de la notificación...'}</p>
                      {image && (
                        <img src={image} alt="" className="mt-2 rounded-lg w-full h-32 object-cover" />
                      )}
                      <p className="text-gray-500 text-xs mt-2">NotiMax • ahora</p>
                    </div>
                  </div>
                </div>
                <p className="text-center text-sm text-gray-500 mt-4">Notificación de ejemplo en Android</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* History */}
        {activeTab === 'history' && (
          <Card>
            <CardHeader>
              <CardTitle>Historial de Notificaciones</CardTitle>
              <CardDescription>Últimas notificaciones enviadas</CardDescription>
            </CardHeader>
            <CardContent>
              {notifications.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No hay notificaciones enviadas aún</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {notifications.map((notif, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-semibold">{notif.title}</p>
                        <p className="text-sm text-gray-600 truncate max-w-md">{notif.body}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(notif.sent_at).toLocaleString('es-MX')}
                        </p>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <div className="flex items-center text-green-600">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            <span className="text-sm">{notif.success_count || 0}</span>
                          </div>
                          <div className="flex items-center text-red-600">
                            <XCircle className="w-4 h-4 mr-1" />
                            <span className="text-sm">{notif.fail_count || 0}</span>
                          </div>
                        </div>
                        <Badge variant={notif.status === 'completed' ? 'default' : 'secondary'}>
                          {notif.status === 'completed' ? 'Enviada' : 'Enviando...'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Settings */}
        {activeTab === 'settings' && (
          <Card>
            <CardHeader>
              <CardTitle>Configuración VAPID</CardTitle>
              <CardDescription>Las claves VAPID son necesarias para enviar notificaciones push</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Importante:</strong> Las claves VAPID solo se generan una vez. 
                  Después de generarlas, debes agregarlas manualmente al archivo <code className="bg-yellow-100 px-1 rounded">.env</code> del servidor.
                </p>
              </div>
              
              <Button onClick={generateVapidKeys} variant="outline">
                Generar Claves VAPID
              </Button>

              <div className="pt-4 border-t">
                <h4 className="font-semibold mb-2">Instrucciones:</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
                  <li>Haz clic en "Generar Claves VAPID"</li>
                  <li>Copia las claves de la consola del navegador (F12)</li>
                  <li>Agrega las líneas al archivo <code>/app/backend/.env</code></li>
                  <li>Reinicia el servidor backend</li>
                  <li>¡Listo! Ya puedes enviar notificaciones</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PushAdmin;
